// src/pages/MeetingReport.js
// Role-based meeting reports: Team Member, Team Leader, Consultant
import { useEffect, useState, useMemo } from 'react';
import styles from './MeetingReport.module.css';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell, ReferenceLine,
  AreaChart, Area, Treemap, ReferenceArea, Legend,
} from 'recharts';
import {
  ALL_CODES, BACKTICK_CODES, SLASH_CODES,
  parseShortcodes, formatDuration, extractTaggedTexts,
} from '../utils/shortcodes';
import {
  buildReportData, MEETING_INVENTORY, SECTION_TYPES,
  DEFAULT_WEIGHTINGS, isInquiry, compressTimeGaps, getMeetingDurationMs,
} from '../utils/metrics';
import { rolesForDisplay } from '../utils/roles';
import AverageTalkTimeChart from '../components/charts/AverageTalkTimeChart';
import CommentsVsQuestionsChart from '../components/charts/CommentsVsQuestionsChart';
import FrequencyChart from '../components/charts/FrequencyChart';
import PageLoading from '../components/PageLoading';

// ─── Shared Primitives ────────────────────────────────────────────────────────

function SectionHeader({ children }) {
  return <div className="report-section-header">{children}</div>;
}

// ─── Roles Display (read-only) ────────────────────────────────────────────────

function RolesDisplay({ categoryKey, meeting }) {
  const observedRoles = meeting.observedRoles || {};
  const allRoles = rolesForDisplay(categoryKey, observedRoles);
  const observedList = allRoles.filter(r => Object.prototype.hasOwnProperty.call(observedRoles, r.id));

  if (observedList.length === 0) return null;

  return (
    <>
      <SectionHeader>Roles Observed</SectionHeader>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {observedList.map((role, i) => {
          const tagged = observedRoles[role.id] || [];
          return (
            <div
              key={role.id}
              style={{
                borderBottom: i < observedList.length - 1 ? '1px solid var(--border)' : 'none',
                paddingBottom: 14,
                paddingTop: i > 0 ? 14 : 0,
              }}
            >
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--ink)', marginBottom: 2 }}>
                {role.name}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', lineHeight: 1.5, marginBottom: tagged.length > 0 ? 8 : 0 }}>
                {role.description}
              </div>
              {tagged.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {tagged.map(name => (
                    <span
                      key={name}
                      style={{
                        padding: '3px 10px',
                        borderRadius: 100,
                        background: 'var(--olive-glow)',
                        color: 'var(--olive)',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                      }}
                    >
                      {name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function Chip({ label, color, emoji, count }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 100,
      background: color + '18', color,
      fontSize: '0.75rem', fontWeight: 600,
      border: `1px solid ${color}33`,
    }}>
      {emoji && <span>{emoji}</span>}{label}{count !== undefined && ` × ${count}`}
    </span>
  );
}

function ScoreBadge({ value, max = 100, size = 'md' }) {
  const pct = max === 100 ? value : Math.round((value / max) * 100);
  const color = pct >= 70 ? 'var(--accent)' : pct >= 40 ? 'var(--warning)' : 'var(--danger)';
  const fontSize = size === 'lg' ? '2.6rem' : '1.8rem';
  return (
    <div className={styles.scoreBadge}>
      <div className={styles.scoreBadgeValue} style={{ fontSize, color }}>
        {value}
        {max !== 100 && <span className={styles.scoreBadgeSuffix}>/{max}</span>}
      </div>
      {max === 100 && <div className={styles.scoreBadgePct}>{pct}%</div>}
    </div>
  );
}

function PersonRow({ name, children }) {
  return (
    <div className={styles.personRow}>
      <div className={styles.personRowName}>
        {name.split('@')[0]}
      </div>
      <div className={styles.personRowChips}>{children}</div>
    </div>
  );
}

// ─── SECTION: AWARDS (all roles) ──────────────────────────────────────────────

function AwardsSection({ awards, entries }) {
  const displayNameMap = {};
  (entries || []).forEach(e => {
    if (e.speakerName) {
      displayNameMap[e.speakerName] = e.speakerDisplayName || e.speakerName.split('@')[0];
    }
  });
  const dn = (name) => displayNameMap[name] || name?.split('@')[0] || name;

  const pairDisplayName = (pair) => {
    if (!pair) return '';
    return pair.split(' ↔ ').map(dn).join(' ↔ ');
  };

  const longestComments = awards.longestComments || (awards.longestComment ? [awards.longestComment] : []);
  const longestMutes = awards.longestMutes || (awards.longestMute ? [awards.longestMute] : []);

  const rankStyle = { fontSize: 11, fontWeight: 700, color: 'var(--ink-muted)', minWidth: 16 };
  const nameStyle = { fontWeight: 600, color: 'var(--ink)', flex: 1 };
  const timeStyle = { fontSize: 12, color: 'var(--ink-muted)', whiteSpace: 'nowrap' };

  return (
    <div className="grid-3" style={{ marginBottom: 24 }}>
      {longestComments.length > 0 && (
        <div className="stat-card">
          <div className="stat-label">🎤 Longest Comments</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {longestComments.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={rankStyle}>{i + 1}.</span>
                <span style={nameStyle}>{dn(item.speaker)}</span>
                <span style={timeStyle}>{formatDuration(item.ms)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {longestMutes.length > 0 && (
        <div className="stat-card">
          <div className="stat-label">🔇 Longest on Mute</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {longestMutes.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={rankStyle}>{i + 1}.</span>
                <span style={nameStyle}>{dn(item.speaker)}</span>
                <span style={timeStyle}>{formatDuration(item.ms)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {awards.longestExchange && (
        <div className="stat-card">
          <div className="stat-label">🏓 Most Back-and-Forth</div>
          <div className={styles.awardsExchangePair} style={{ marginTop: 8 }}>
            {pairDisplayName(awards.longestExchange.pair)}
          </div>
          <div className="stat-sub">
            {awards.longestExchange.turns} turns
            {awards.longestExchange.ms ? ` · ${formatDuration(awards.longestExchange.ms)}` : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SECTION: ADDITIONAL NOTES ────────────────────────────────────────────────

function extractTagItems(entries, tag) {
  const items = [];
  (entries || []).forEach(entry => {
    extractTaggedTexts(entry.text || '', tag).forEach(text => {
      items.push({
        speaker: entry.speakerDisplayName || entry.speakerName?.split('@')[0] || '—',
        text,
      });
    });
  });
  return items;
}

const extractFeedbackItems = (entries) => extractTagItems(entries, '/f');
const extractKeyMoments = (entries) => extractTagItems(entries, '/km');

function AdditionalNotesSection({ awards, entries, meeting }) {
  const feedbackItems = extractFeedbackItems(entries);
  const keyMoments = extractKeyMoments(entries);
  const compiledFeedback = meeting?.meetingFeedback;
  const participantNotes = Object.entries(meeting?.participantFeedback || {})
    .filter(([, d]) => d.notes?.trim())
    .map(([speakerKey, d]) => ({ name: d.displayName || speakerKey.split('@')[0], notes: d.notes.trim() }));

  return (
    <>
      {compiledFeedback && (
        <>
          <SectionHeader>Meeting Feedback</SectionHeader>
          <div style={{ padding: '14px 16px', background: 'var(--bg-soft)', borderRadius: 8, marginBottom: 24, fontSize: '0.875rem', color: 'var(--ink)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {compiledFeedback}
          </div>
        </>
      )}

      {participantNotes.length > 0 && (
        <>
          <SectionHeader>Participant Coaching</SectionHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
            {participantNotes.map(({ name, notes }) => (
              <div key={name} style={{ padding: '12px 16px', background: 'var(--bg-soft)', borderRadius: 8, borderLeft: '4px solid #9b5de5' }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#9b5de5', marginBottom: 6 }}>📝 {name}</div>
                <div style={{ fontSize: '0.875rem', color: 'var(--ink)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{notes}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <SectionHeader>Notable Moments</SectionHeader>
      <AwardsSection awards={awards} entries={entries} />

      {feedbackItems.length > 0 && (
        <>
          <SectionHeader>Feedback</SectionHeader>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
            <thead>
              <tr>
                {['Speaker', 'Feedback'].map(col => (
                  <th key={col} style={{
                    padding: '8px 12px', textAlign: col === 'Speaker' ? 'left' : 'left',
                    fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.5px',
                    color: 'var(--ink-muted)', borderBottom: '2px solid var(--border)',
                  }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {feedbackItems.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-soft)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600, fontSize: '0.82rem', color: 'var(--ink)', whiteSpace: 'nowrap', width: '20%' }}>
                    {item.speaker}
                  </td>
                  <td style={{ padding: '8px 12px', fontSize: '0.82rem', color: 'var(--ink)' }}>
                    {item.text}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {keyMoments.length > 0 && (
        <>
          <SectionHeader>Key Moments</SectionHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
            {keyMoments.map((item, i) => (
              <div key={i} style={{
                padding: '10px 14px',
                borderLeft: '3px solid #4361ee',
                background: 'var(--bg-soft)',
                borderRadius: '0 6px 6px 0',
              }}>
                <div style={{ fontSize: '0.82rem', color: 'var(--ink)' }}>{item.text}</div>
                {item.speaker && item.speaker !== '—' && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 4, fontWeight: 600 }}>
                    {item.speaker}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ─── SECTION: SIMPLE PROCESS ──────────────────────────────────────────────────

// Simple Process shows the section feedback text from the inventory.
// The sectionFeedback keys are the section *names* (e.g. "Lightning Round"),
// not the SECTION_TYPES ids.
function SimpleProcessSection({ meeting }) {
  const sectionFeedback = meeting.sectionFeedback || {};
  const sectionNames = Object.keys(sectionFeedback);

  return (
    <div className={styles.sectionList}>
      {sectionNames.map((name, idx) => {
        const fb = sectionFeedback[name] || '';
        return (
          <div key={name} className={styles.sectionItem}>
            <div className={styles.sectionName} style={{ marginBottom: fb ? 10 : 0 }}>
              § {name}
            </div>
            {fb ? (
              <p className={styles.sectionText}>
                {fb}
              </p>
            ) : (
              <p className={styles.sectionEmpty}>
                No feedback added for this section.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

const PROCESS_CODE_PAIRS_DISPLAY = [
  { base: '/r', label: 'Roles',          emoji: '👤' },
  { base: '/u', label: 'Rules',          emoji: '📏' },
  { base: '/p', label: 'Parts',          emoji: '🧩' },
  { base: '/t', label: 'Tools',          emoji: '🔧' },
  { base: '/m', label: 'Messages',       emoji: '✉️' },
  { base: '/a', label: 'Actions',        emoji: '📌' },
];

function ProcessCodesTally({ processCodes }) {
  if (!processCodes) return null;
  const tagged = PROCESS_CODE_PAIRS_DISPLAY.filter(({ base }) => (processCodes[base]?.total || 0) > 0);
  if (tagged.length === 0) return null;

  return (
    <div style={{ marginBottom: 28 }}>
      <SectionHeader>Clear Process</SectionHeader>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {tagged.map(({ base, label, emoji }) => {
          const { positive = 0, total = 0 } = processCodes[base] || {};
          const ratio = total > 0 ? positive / total : 0;
          const scoreColor = ratio >= 0.8 ? 'var(--accent)' : ratio >= 0.5 ? 'var(--warning)' : 'var(--danger)';
          return (
            <div key={base} style={{ textAlign: 'center', padding: '10px 16px', background: 'var(--bg-soft)', borderRadius: 10, minWidth: 76 }}>
              <div style={{ fontSize: '1.4rem', marginBottom: 4 }}>{emoji}</div>
              <div style={{ fontSize: '1.15rem', fontWeight: 700, color: scoreColor, lineHeight: 1 }}>
                {positive}<span style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>/{total}</span>
              </div>
              <div style={{ fontSize: '0.62rem', color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 4 }}>
                {label}
              </div>
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 10, marginBottom: 0 }}>
        X/Y — times observed out of total tagged opportunities. Tagged with /r, /u, /p, /t, /m, /a during recording.
      </p>
    </div>
  );
}

function SimpleProcessTeamMember({ meeting }) {
  const hasSectionFeedback = Object.keys(meeting.sectionFeedback || {}).length > 0;
  if (!hasSectionFeedback) return null;
  return (
    <div>
      <SectionHeader>Section Feedback</SectionHeader>
      <SimpleProcessSection meeting={meeting} />
    </div>
  );
}

function SimpleProcessTeamLeader({ meeting, processCodes }) {
  const hasSectionFeedback = Object.keys(meeting.sectionFeedback || {}).length > 0;
  return (
    <div>
      <ProcessCodesTally processCodes={processCodes} />
      {hasSectionFeedback && (
        <>
          <SectionHeader>Section Feedback</SectionHeader>
          <SimpleProcessSection meeting={meeting} />
        </>
      )}
    </div>
  );
}

function SimpleProcessConsultant({ meeting, processCodes }) {
  return <SimpleProcessTeamLeader meeting={meeting} processCodes={processCodes} />;
}

// ─── SECTION: COMPELLING CONTENT ─────────────────────────────────────────────

// Speakers-per-topic bar chart + dialogue frequency line chart
function BandLabel({ viewBox, value }) {
  if (!value || !viewBox) return null;
  const { x, y, width } = viewBox;
  if (width < 20) return null;
  const label = value.length > 22 ? value.slice(0, 21) + '…' : value;
  const cx = x + width / 2;
  const cy = y + 4;
  return (
    <text x={cx} y={cy} transform={`rotate(90, ${cx}, ${cy})`} textAnchor="start" dominantBaseline="middle" fontSize={8} fill="#666">
      {label}
    </text>
  );
}

function KeyMomentStar({ viewBox }) {
  if (!viewBox) return null;
  const { x, y } = viewBox;
  return (
    <text x={x} y={(y || 0) + 14} textAnchor="middle" fontSize={13} fill="#4361ee">★</text>
  );
}

function TopicEngagementCharts({ topics, entries, showLineChart = true, showBarChart = true }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const id = setTimeout(() => setMounted(true), 0); return () => clearTimeout(id); }, []);

  const COLORS = ['#2a3d5b', '#5f7942', '#cba43a', '#922b3f', '#7209b7', '#4cc9f0', '#982c4c', '#54a09f'];
  const BAND_COLORS = ['#2a3d5b12', '#5f794212', '#cba43a12', '#922b3f12', '#7209b712', '#4cc9f012'];
  const WINDOW_MS = 2 * 60 * 1000;

  const barData = useMemo(() => topics.map(t => ({
    name: t.label.length > 18 ? t.label.slice(0, 16) + '…' : t.label,
    fullName: t.label,
    speakers: t.speakers,
    questions: t.questions,
  })), [topics]);

  const { lineData, topicBands, kmTimes } = useMemo(() => {
    const sorted = compressTimeGaps(entries)
      .filter(e => e.startTime)
      .sort((a, b) => a.startTime - b.startTime);
    const nonBreak = sorted.filter(e => e.type !== 'break');

    const lineData = [];
    const topicBands = [];
    const kmTimes = [];

    if (nonBreak.length > 0) {
      const origin = nonBreak[0].startTime;
      const last = nonBreak[nonBreak.length - 1];
      const totalCompressed = (last.endTime || last.startTime) - origin;
      const numBuckets = Math.max(Math.ceil(totalCompressed / WINDOW_MS), 1);

      for (let b = 0; b < numBuckets; b++) {
        const bucketCompMid = origin + b * WINDOW_MS + WINDOW_MS / 2;
        const bucket = { time: `${Math.round((b * WINDOW_MS) / 60000)}m` };

        let activeSection = null;
        let activeTopic = null;
        for (let i = nonBreak.length - 1; i >= 0; i--) {
          if (nonBreak[i].startTime <= bucketCompMid) {
            if (!activeSection && nonBreak[i].section) activeSection = nonBreak[i].section;
            if (!activeTopic && nonBreak[i].topic) activeTopic = nonBreak[i].topic;
            if (activeSection || activeTopic) break;
          }
        }
        if (activeSection) bucket._section = activeSection;
        if (activeTopic) bucket._topic = activeTopic;

        const speakersInWindow = new Set();
        let questionsInWindow = 0;
        nonBreak.forEach(e => {
          if (!e.speakerName) return;
          const relTime = e.startTime - origin;
          if (relTime >= b * WINDOW_MS && relTime < (b + 1) * WINDOW_MS) {
            speakersInWindow.add(e.speakerDisplayName || e.speakerName.split('@')[0]);
            if (isInquiry(e)) questionsInWindow++;
          }
        });
        bucket.uniqueSpeakers = speakersInWindow.size;
        bucket.questionsInWindow = questionsInWindow;
        lineData.push(bucket);
      }

      if (lineData.length > 0) {
        let currentLabel = null;
        let bandStartIdx = 0;
        lineData.forEach((d, i) => {
          const label = d._topic || d._section || null;
          if (label !== currentLabel) {
            if (currentLabel !== null) {
              topicBands.push({ label: currentLabel, x1: lineData[bandStartIdx].time, x2: lineData[i - 1].time, colorIdx: topicBands.length });
            }
            currentLabel = label;
            bandStartIdx = i;
          }
          if (i === lineData.length - 1 && currentLabel !== null) {
            topicBands.push({ label: currentLabel, x1: lineData[bandStartIdx].time, x2: d.time, colorIdx: topicBands.length });
          }
        });
      }

      nonBreak.forEach(e => {
        if (e.text?.includes('/km') && e.startTime) {
          const bucketIdx = Math.min(
            Math.floor((e.startTime - origin) / WINDOW_MS),
            lineData.length - 1
          );
          const label = lineData[bucketIdx]?.time;
          if (label && !kmTimes.includes(label)) kmTimes.push(label);
        }
      });
    }

    return { lineData, topicBands, kmTimes };
  }, [entries]);

  if (topics.length === 0) return null;
  if (!mounted) return <div style={{ minHeight: 460 }} />;

  return (
    <>
      {showBarChart && (
      <>
      <SectionHeader>Speakers per Topic</SectionHeader>
      <p className={styles.chartDesc}>
        Number of unique speakers who contributed to each topic
      </p>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={barData} margin={{ left: -10, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11 }}
            angle={-35}
            textAnchor="end"
            interval={0}
            height={70}
          />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip
            formatter={(val, name) => [val, 'Speakers']}
            labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
          />
          <Bar dataKey="speakers" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {barData.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      </>
      )}

      {showLineChart && lineData.length > 1 && (
        <>
          <SectionHeader>Dialogue Frequency Over Time</SectionHeader>
          <p className={styles.chartDesc}>
            Unique speakers per 2-minute window
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={lineData} margin={{ left: -10 }}>
              <defs>
                <linearGradient id="speakersGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2a3d5b" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#2a3d5b" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="questionsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#cba43a" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#cba43a" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="time" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} domain={[0, 'auto']} />
              <Tooltip
                formatter={(val, name) => [val, name === 'uniqueSpeakers' ? 'Unique speakers' : 'Questions asked']}
                labelFormatter={(label) => `At ${label}`}
              />
              {topicBands.map((band, i) => (
                <ReferenceArea
                  key={i}
                  x1={band.x1}
                  x2={band.x2}
                  fill={BAND_COLORS[band.colorIdx % BAND_COLORS.length]}
                  label={<BandLabel value={band.label} />}
                />
              ))}
              {kmTimes.map((t, i) => (
                <ReferenceLine
                  key={`km-${i}`}
                  x={t}
                  stroke="#4361ee"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  label={{ value: '★', position: 'insideTop', fill: '#4361ee', fontSize: 11 }}
                />
              ))}
              <Area
                type="monotone"
                dataKey="uniqueSpeakers"
                stroke="#2a3d5b"
                strokeWidth={2.5}
                fill="url(#speakersGrad)"
                dot={false}
                activeDot={{ r: 4, fill: '#2a3d5b' }}
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="questionsInWindow"
                stroke="#cba43a"
                strokeWidth={1.5}
                fill="url(#questionsGrad)"
                dot={false}
                activeDot={{ r: 4, fill: '#cba43a' }}
                isAnimationActive={false}
              />
              <Legend
                content={(props) => {
                  const items = (props.payload || []).map(p => ({
                    color: p.color,
                    label: p.dataKey === 'uniqueSpeakers' ? 'Unique speakers' : 'Questions asked',
                  }));
                  return (
                    <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', fontSize: 11, paddingTop: 4 }}>
                      {items.map((item, i) => (
                        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ display: 'inline-block', width: 12, height: 3, background: item.color, borderRadius: 2 }} />
                          {item.label}
                        </span>
                      ))}
                      {kmTimes.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ color: '#4361ee', fontSize: 13, lineHeight: 1 }}>★</span>
                          Key Moments
                        </span>
                      )}
                    </div>
                  );
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </>
  );
}

function CompellingContentSection({ reportData, meeting }) {
  const { topics } = reportData;
  // topicFeedback is keyed by topic name string (e.g. "Budget Review")
  const topicFeedback = meeting.topicFeedback || {};

  // Aggregate stats from topicFeedback
  const feedbackValues = topics.map(t => topicFeedback[t.label]).filter(Boolean);
  const decisionScores = feedbackValues.map(f => f.decision).filter(s => s && s !== 'na' && s > 0);
  const debateScores = feedbackValues.map(f => f.debate).filter(s => s && s !== 'na' && s > 0);
  const avgDecision = decisionScores.length
    ? (decisionScores.reduce((a, b) => a + b, 0) / decisionScores.length).toFixed(1)
    : '—';
  const avgDebate = debateScores.length
    ? (debateScores.reduce((a, b) => a + b, 0) / debateScores.length).toFixed(1)
    : '—';
  const totalQuestions = topics.reduce((sum, t) => sum + (t.questions || 0), 0);

  return (
    <div>
      <SectionHeader>Topics & Decisions</SectionHeader>
      <div className="grid-3" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">Topics Discussed</div>
          <div className="stat-value">{topics.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Level of Debate</div>
          <div className="stat-value">
            {avgDebate}
            {avgDebate !== '—' && <span className={styles.statSuffix}>/5</span>}
          </div>
          <div className="stat-sub">avg across topics</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Level of Decision</div>
          <div className="stat-value">
            {avgDecision}
            {avgDecision !== '—' && <span className={styles.statSuffix}>/5</span>}
          </div>
          <div className="stat-sub">avg across topics</div>
        </div>
      </div>

      {topics.length > 0 && (
        <>
          <SectionHeader>Topic Breakdown</SectionHeader>
          <div className={styles.topicList}>
            {topics.map((topic, i) => {
              const fb = topicFeedback[topic.label] || {};
              const decScore = fb.decision || 0;
              const debScore = fb.debate || 0;
              const decColor = decScore === 5 ? 'var(--warning)' : decScore >= 3 ? 'var(--accent)' : decScore >= 1 ? 'var(--danger)' : 'var(--ink-muted)';
              const debColor = debScore === 5 ? 'var(--warning)' : debScore >= 3 ? 'var(--accent)' : debScore >= 1 ? 'var(--danger)' : 'var(--ink-muted)';

              // Per-speaker time within this topic
              const speakerMap = {};
              (topic.entries || []).forEach(e => {
                if (!e.speakerName) return;
                const name = e.speakerDisplayName || e.speakerName.split('@')[0];
                const ms = (e.endTime && e.startTime) ? e.endTime - e.startTime : 0;
                speakerMap[name] = (speakerMap[name] || 0) + ms;
              });
              const speakerList = Object.entries(speakerMap).sort((a, b) => b[1] - a[1]);

              // Genius tags used during this topic
              const geniusList = Object.entries(topic.genius || {})
                .map(([code, count]) => ({ code, count, def: ALL_CODES[code] }))
                .filter(g => g.def && g.count > 0);

              return (
                <div key={i} className={styles.topicItem}>
                  <div className={styles.topicItemHeader}>
                    <div className={styles.topicLabel}>
                      {topic.label}
                    </div>
                    <div className={styles.topicScores}>
                      {debScore > 0 && (
                        <div className={styles.topicScoreCol}>
                          <div className={styles.topicScoreVal} style={{ color: debColor }}>
                            {debScore}<span className={styles.topicScoreSuffix}>/5</span>
                          </div>
                          <div className={styles.topicScoreLabel}>Debate</div>
                        </div>
                      )}
                      {decScore > 0 && (
                        <div className={styles.topicScoreCol}>
                          <div className={styles.topicScoreVal} style={{ color: decColor }}>
                            {decScore}<span className={styles.topicScoreSuffix}>/5</span>
                          </div>
                          <div className={styles.topicScoreLabel}>Decision</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.topicStats}>
                    <span>⏱ {formatDuration(topic.ms)}</span>
                    <span>👥 {topic.speakers} speaker{topic.speakers !== 1 ? 's' : ''}</span>
                    <span>❓ {topic.questions} question{topic.questions !== 1 ? 's' : ''}</span>
                  </div>
                  {speakerList.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                      {speakerList.map(([name, ms]) => (
                        <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: '0.75rem', color: 'var(--ink)' }}>
                          {name}
                          {ms > 0 && <span style={{ color: 'var(--ink-muted)' }}>· {formatDuration(ms)}</span>}
                        </span>
                      ))}
                    </div>
                  )}
                  {geniusList.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                      {geniusList.map(({ code, count, def }) => (
                        <span key={code} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', background: def.color + '1a', border: `1px solid ${def.color}33`, borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, color: def.color }}>
                          {def.emoji} {def.label}{count > 1 ? ` ×${count}` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function CompellingContentTeamMember({ reportData }) {
  const { topics } = reportData;
  return (
    <div>
      <SectionHeader>Topics Discussed</SectionHeader>
      {topics.length === 0 ? (
        <p className={styles.mutedNote}>No topics were recorded.</p>
      ) : (
        <div className={styles.topicListSm}>
          {topics.map((t, i) => (
            <div key={i} className={styles.topicRowSm}>
              <span className={styles.topicNameSm}>{t.label}</span>
              {t.ms > 0 && (
                <span className={styles.topicDuration}>
                  {formatDuration(t.ms)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CompellingContentTeamLeader({ reportData, meeting, entries, showBarChart = true }) {
  return (
    <div>
      <CompellingContentSection reportData={reportData} meeting={meeting} />
      <TopicEngagementCharts topics={reportData.topics} entries={entries} showBarChart={showBarChart} />
    </div>
  );
}

function CompellingContentConsultant({ reportData, meeting, entries }) {
  return <CompellingContentTeamLeader reportData={reportData} meeting={meeting} entries={entries} />;
}

// ─── CHART: FREQUENCY ─────────────────────────────────────────────────────────
// (AverageTalkTimeChart, CommentsVsQuestionsChart, FrequencyChart extracted to src/components/charts/)

// ─── CHART: BREVITY PER PERSON ────────────────────────────────────────────────

function BrevityPerPersonChart({ participation, nameMap = {} }) {
  const speakers = participation.speakers.filter(p => p.turns > 0 && p.ms > 0);
  if (speakers.length === 0) return null;

  const data = speakers.map(p => {
    const avgSec = Math.round((p.ms / 1000) / p.turns);
    return {
      name: nameMap[p.name] || p.name.split('@')[0],
      seconds: avgSec,
      color: avgSec < 20 ? '#2a3d5b' : avgSec <= 30 ? '#5f7942' : '#cba43a',
    };
  }).sort((a, b) => b.seconds - a.seconds);

  const chartHeight = Math.max(100, data.length * 48 + 40);

  return (
    <div style={{ marginTop: 32 }}>
      <SectionHeader>Brevity — Avg. Seconds per Turn</SectionHeader>
      <p style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', marginBottom: 16 }}>
        Each person's average air time per contribution. Target range: <strong>20–30 seconds</strong>.
      </p>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 50, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
          <XAxis type="number" domain={[0, 'auto']} unit="s" tick={{ fontSize: 11, fill: 'var(--ink-muted)' }} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: 'var(--ink)' }} width={70} />
          <Tooltip formatter={v => [`${v}s`, 'Avg. per turn']} />
          <ReferenceLine x={20} stroke="#5f7942" strokeDasharray="5 3"
            label={{ value: '20s', position: 'insideTopRight', fontSize: 10, fill: '#5f7942' }} />
          <ReferenceLine x={30} stroke="#cba43a" strokeDasharray="5 3"
            label={{ value: '30s', position: 'insideTopRight', fontSize: 10, fill: '#cba43a' }} />
          <Bar dataKey="seconds" radius={[0, 4, 4, 0]} label={{ position: 'right', formatter: v => `${v}s`, fontSize: 11, fill: 'var(--ink-muted)' }}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', fontSize: '0.75rem', color: 'var(--ink-muted)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#2a3d5b' }} /> Too brief (&lt; 20s)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#5f7942' }} /> On target (20–30s)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#cba43a' }} /> Too long (&gt; 30s)
        </div>
      </div>
    </div>
  );
}

// ─── SECTION: HEALTHY BEHAVIORS ───────────────────────────────────────────────

function HealthyBehaviorsTeamMember({ reportData, entries = [], showAvgTalkTime = false, showParticipationBars = false, showFrequency = false, showBrevity = false, showStatsTable = true }) {
  const { participation } = reportData;
  const colors = ['#2a3d5b', '#5f7942', '#cba43a', '#922b3f', '#7209b7', '#4cc9f0'];

  // Build display name map: speakerName → displayName
  const nameMap = {};
  entries.forEach(e => {
    if (e.speakerName) {
      nameMap[e.speakerName] = e.speakerDisplayName || e.speakerName.split('@')[0];
    }
  });
  const getName = (rawName) => nameMap[rawName] || rawName.split('@')[0];

  // Build frequency score lookup: speakerName → score
  const freqMap = {};
  (reportData.frequency || []).forEach(f => { freqMap[f.speaker] = f.score; });

  // Treemap-like visual: occupying meeting space
  const treemapData = participation.speakers
    .slice()
    .sort((a, b) => (b.pct || 0) - (a.pct || 0))
    .map((p, i) => ({
      name: getName(p.name),
      pct: p.pct,
      color: colors[i % colors.length],
  }));

  return (
    <div>
      <SectionHeader>Occupying Meeting Space</SectionHeader>
      <ResponsiveContainer width="100%" height={320} style={{ marginBottom: 40 }}>
        <Treemap
          data={treemapData.map(item => ({ ...item, size: item.pct || 1 }))}
          dataKey="size"
          aspectRatio={16 / 9}
          isAnimationActive={false}
          content={({ x, y, width, height, name, pct, color }) => {
            const showLabel = width > 40 && height > 28;
            const showPct = width > 50 && height > 44;
            // Progressively shorten name to fit: Full Name → First Name → Initial
            if (!name) return <g />;
            const parts = name.split(' ');
            const displayName = width > 110 || parts.length === 1
              ? name
              : width > 60
              ? parts[0]
              : parts[0]?.charAt(0);
            return (
              <g>
                <rect
                  x={x + 1} y={y + 1}
                  width={Math.max(width - 2, 0)}
                  height={Math.max(height - 2, 0)}
                  style={{ fill: color, rx: 6, ry: 6 }}
                  rx={6} ry={6}
                />
                {showLabel && (
                  <text
                    x={x + width / 2} y={y + height / 2 - (showPct ? 10 : 0)}
                    textAnchor="middle" dominantBaseline="middle"
                    style={{ fill: 'white', fontSize: showPct ? '0.82rem' : '0.75rem', fontWeight: 700, fontFamily: 'var(--font-body)' }}
                  >
                    {displayName}
                  </text>
                )}
                {showPct && (
                  <text
                    x={x + width / 2} y={y + height / 2 + 12}
                    textAnchor="middle" dominantBaseline="middle"
                    style={{ fill: 'rgba(255,255,255,0.85)', fontSize: '1rem', fontWeight: 600, fontFamily: 'var(--font-body)' }}
                  >
                    {pct}%
                  </text>
                )}
              </g>
            );
          }}
        />
      </ResponsiveContainer>

      {showStatsTable && (
        <>
          <SectionHeader>Participant Statistics</SectionHeader>
          <div className={styles.tableWrap}>
            <table className={styles.statsTable}>
              <thead>
                <tr className={styles.statsTheadRow}>
                  {['Name', 'Comments', 'Questions', 'Freq. #', 'Air Time'].map(col => (
                    <th key={col} className={styles.statsTh} style={{ textAlign: col === 'Name' ? 'left' : 'center' }}>
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {participation.speakers.map((p, i) => {
                  const inq = reportData.inquiry.perPerson.find(x => x.name === p.name) || { advocacy: 0, inquiry: 0 };
                  return (
                    <tr key={p.name} className={styles.statsTr} style={{
                      borderBottom: '1px solid var(--border)',
                      background: i % 2 === 0 ? 'transparent' : 'var(--bg-soft)',
                    }}>
                      <td className={styles.statsNameTd} style={{ color: colors[i % colors.length] }}>
                        <div className={styles.statsNameInner}>
                          <div className={styles.statsColorDot} style={{ background: colors[i % colors.length] }} />
                          {getName(p.name)}
                        </div>
                      </td>
                      <td className={styles.statsTdCenter}>{inq.advocacy}</td>
                      <td className={styles.statsTdCenter}>{inq.inquiry}</td>
                      <td className={styles.statsTdCenter}>{freqMap[p.name] ?? p.turns}</td>
                      <td className={styles.statsTdAirTime}>
                        {formatDuration(p.ms)}
                        <span className={styles.statsTdPctSuffix}>({p.pct}%)</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showAvgTalkTime && <AverageTalkTimeChart participation={participation} colors={colors} nameMap={nameMap} />}

      {showParticipationBars && (
        <>
          <SectionHeader>Meeting Participation</SectionHeader>
          <p className={styles.chartDesc}>
            Total talk time by participant
          </p>
          <div className={styles.participationList}>
            {participation.speakers.map((p, i) => (
              <div key={p.name}>
                <div className={styles.participationRowHeader}>
                  <span className={styles.participationName}>{getName(p.name)}</span>
                  <div className={styles.participationStats}>
                    <span>{p.turns} turn{p.turns !== 1 ? 's' : ''}</span>
                    <span>{formatDuration(p.ms)}</span>
                    <span className={styles.participationPct}>{p.pct}%</span>
                  </div>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${p.pct}%`, background: colors[i % colors.length] }} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <CommentsVsQuestionsChart inquiry={reportData.inquiry} nameMap={nameMap} />

      {showFrequency && (
        <FrequencyChart frequency={reportData.frequency} nameMap={nameMap} />
      )}

      {showBrevity && (
        <BrevityPerPersonChart participation={participation} nameMap={nameMap} />
      )}
    </div>
  );
}

function HealthyBehaviorsTeamLeader({ reportData, entries }) {
  return <HealthyBehaviorsTeamMember reportData={reportData} entries={entries} showFrequency={true} showBrevity={true} />;
}

function HealthyBehaviorsConsultant({ reportData, entries }) {
  return <HealthyBehaviorsTeamMember reportData={reportData} entries={entries} showAvgTalkTime={true} showFrequency={true} showBrevity={true} />;
}

// ─── SECTION: THE RIGHT GENIUS ────────────────────────────────────────────────

function TheRightGeniusTeamMember({ reportData, topics, showByTopic = false, showCharts = false }) {
  const { bySpeaker, totals, grandTotal } = reportData.genius;
  const colors = Object.values(BACKTICK_CODES).map(d => d.color);

  if (grandTotal === 0) {
    return <p className={styles.mutedNote}>No Right Chemistry tags were recorded.</p>;
  }

  const CANONICAL_ORDER = ['`w', '`i', '`d', '`g', '`e', '`t'];

  // Per person — include all speakers who tagged at least one genius
  const bySpeakerData = Object.entries(bySpeaker)
    .filter(([, codes]) => Object.keys(codes).length > 0)
    .sort((a, b) => Object.values(b[1]).reduce((x, y) => x + y, 0) - Object.values(a[1]).reduce((x, y) => x + y, 0));

  return (
    <div>
      <SectionHeader>Right Chemistry per Person</SectionHeader>
      {bySpeakerData.map(([speaker, codes]) => {
        const maxCount = Math.max(1, ...CANONICAL_ORDER.map(code => codes[code] || 0));
        return (
          <PersonRow key={speaker} name={speaker}>
            {CANONICAL_ORDER.map(code => {
              const def = BACKTICK_CODES[code];
              if (!def) return null;
              const count = codes[code] || 0;
              const pct = (count / maxCount) * 100;
              return (
                <div key={code} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4, opacity: count === 0 ? 0.5 : 1 }}>
                  <Chip label={def.label} color={def.color} emoji={def.emoji} count={count} />
                  <div style={{ width: '100%', height: 4, background: 'var(--border)', borderRadius: 2 }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: def.color, borderRadius: 2, opacity: count === 0 ? 0 : 1 }} />
                  </div>
                </div>
              );
            })}
          </PersonRow>
        );
      })}

      {showByTopic && (
        <>
          <SectionHeader>Right Chemistry by Topic</SectionHeader>
          {topics.filter(t => Object.values(t.genius).some(v => v > 0)).length > 0 ? (
            topics
              .filter(t => Object.values(t.genius).some(v => v > 0))
              .map((topic, i) => {
                const maxCount = Math.max(1, ...CANONICAL_ORDER.map(code => topic.genius[code] || 0));
                return (
                  <PersonRow key={i} name={topic.label}>
                    {CANONICAL_ORDER.map(code => {
                      const def = BACKTICK_CODES[code];
                      if (!def) return null;
                      const count = topic.genius[code] || 0;
                      const pct = (count / maxCount) * 100;
                      return (
                        <div key={code} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4, opacity: count === 0 ? 0.5 : 1 }}>
                          <Chip label={def.label} color={def.color} emoji={def.emoji} count={count} />
                          <div style={{ width: '100%', height: 4, background: 'var(--border)', borderRadius: 2 }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: def.color, borderRadius: 2, opacity: count === 0 ? 0 : 1 }} />
                          </div>
                        </div>
                      );
                    })}
                  </PersonRow>
                );
              })
          ) : (
            <p className={styles.mutedNote}>No genius tags by topic.</p>
          )}
        </>
      )}

      {showCharts && (
        <>
          <SectionHeader>Right Chemistry Usage Summary</SectionHeader>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={['`w', '`i', '`d', '`g', '`e', '`t'].map(code => ({
                name: BACKTICK_CODES[code].label,
                count: totals[code] || 0,
              }))}
              margin={{ left: -10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {['`w', '`i', '`d', '`g', '`e', '`t'].map(code => (
                  <Cell key={code} fill={BACKTICK_CODES[code].color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

        </>
      )}
    </div>
  );
}

function TheRightGeniusTeamLeader({ reportData, topics }) {
  return <TheRightGeniusTeamMember reportData={reportData} topics={topics} showByTopic={true} />;
}

function TheRightGeniusConsultant({ reportData, topics }) {
  return <TheRightGeniusTeamMember reportData={reportData} topics={topics} showByTopic={true} showCharts={true} />;
}

// ─── SECTION: QUOTES (Team Leader + Consultant only) ────────────────────────

function QuotesTeamLeader({ reportData, meeting }) {
  // Team Leader: Curated quotes selected by consultant (in meeting.consultantQuotes)
  const { productive, unproductive } = reportData.quotes;
  const selected = meeting?.selectedQuotes || {};
  const hasAnySelected = Object.values(selected).some(Boolean);
  const shownProductive = hasAnySelected
    ? productive.filter((_, i) => selected[`prod-${i}`])
    : [];
  const shownUnproductive = hasAnySelected
    ? unproductive.filter((_, i) => selected[`unprod-${i}`])
    : [];

  return (
    <div>
      {shownProductive.length > 0 && (
        <>
          <SectionHeader>🎙️ Sound Bites</SectionHeader>
          <div className={styles.quoteListMb}>
            {shownProductive.map((q, i) => (
              <div key={i} className={styles.quoteProductive}>
                "{q.text}" <span className={styles.quoteAttribution}>— {q.speaker?.split('@')[0]}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {shownUnproductive.length > 0 && (
        <>
          <SectionHeader>🚩 Caution Flags</SectionHeader>
          <div className={styles.quoteList}>
            {shownUnproductive.map((q, i) => (
              <div key={i} className={styles.quoteUnproductive}>
                '{q.text}' <span className={styles.quoteAttribution}>— {q.speaker?.split('@')[0]}</span>
              </div>
            ))}
          </div>
        </>
      )}
      {shownProductive.length === 0 && shownUnproductive.length === 0 && (
        <p className={styles.mutedNote}>
          {hasAnySelected ? 'No quotes selected for sharing.' : 'No quotes have been selected for sharing yet.'}
        </p>
      )}
    </div>
  );
}

function QuotesConsultant({ reportData, entries, meeting }) {
  // Consultant: All quotes with checkboxes to include/exclude
  const { productive, unproductive } = reportData.quotes;
  const [selectedQuotes, setSelectedQuotes] = useState(meeting.selectedQuotes || {});

  const allQuotes = [
    ...productive.map((q, i) => ({ ...q, id: `prod-${i}`, type: 'productive' })),
    ...unproductive.map((q, i) => ({ ...q, id: `unprod-${i}`, type: 'unproductive' })),
  ];

  const toggleQuote = async (quoteId) => {
    const newState = { ...selectedQuotes, [quoteId]: !selectedQuotes[quoteId] };
    setSelectedQuotes(newState);
    try {
      await updateDoc(doc(db, 'meetings', meeting.id), { selectedQuotes: newState });
    } catch (err) {
      console.error('Failed to save quote selection:', err);
    }
  };

  return (
    <div>
      <SectionHeader>Quotes (select to share)</SectionHeader>
      <div className={styles.quoteSelectList}>
        {allQuotes.map(q => (
          <label key={q.id} className={styles.quoteSelectLabel} style={{
            borderLeft: `3px solid ${q.type === 'productive' ? '#5f7942' : '#922b3f'}`,
          }}>
            <input
              type="checkbox"
              checked={selectedQuotes[q.id] || false}
              onChange={() => toggleQuote(q.id)}
              className={styles.quoteCheckbox}
            />
            <div className={styles.quoteSelectContent}>
              <div className={styles.quoteSelectText}>
                {q.type === 'productive' ? '"' : "'"}{q.text}{q.type === 'productive' ? '"' : "'"}
              </div>
              <div className={styles.quoteAttribution}>
                — {q.speaker?.split('@')[0]}
              </div>
            </div>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: q.type === 'productive' ? '#5f7942' : '#922b3f' }}>
              {q.type === 'productive' ? '🎙️ Sound Bite' : '🚩 Caution Flag'}
            </span>
          </label>
        ))}
      </div>
      {allQuotes.length === 0 && (
        <p className={styles.mutedNote}>No quotes captured in this meeting.</p>
      )}
    </div>
  );
}

// ─── MAIN REPORT PAGE ─────────────────────────────────────────────────────────

export default function MeetingReport() {
  const { meetingId } = useParams();
  const { userProfile } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('compelling_content');
  const [generating, setGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
  const navigate = useNavigate();

  // Determine role
  const role = userProfile?.role;
  const isTeamLeaderOrAbove = ['team_leader', 'admin'].includes(role);
  const isConsultant = ['consultant', 'admin'].includes(role);

  useEffect(() => { loadMeeting(); }, [meetingId]);

  async function loadMeeting() {
    try {
      const snap = await getDoc(doc(db, 'meetings', meetingId));
      if (snap.exists()) setMeeting({ id: snap.id, ...snap.data() });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function removeAttendee(attendee) {
    const updatedAttendees = (meeting.attendees || []).filter(
      a => (a.id || a.name) !== (attendee.id || attendee.name)
    );
    try {
      await updateDoc(doc(db, 'meetings', meeting.id), { attendees: updatedAttendees });
      setMeeting(prev => ({ ...prev, attendees: updatedAttendees }));
    } catch (err) {
      console.error('Failed to remove attendee:', err);
    }
  }

  useEffect(() => {
    if (!pdfDropdownOpen) return;
    const close = () => setPdfDropdownOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [pdfDropdownOpen]);

  const handleDownloadPDF = async (reportType = 'full') => {
    setPdfDropdownOpen(false);
    setGenerating(true);
    setPdfError(null);
    try {
      const { getAuth } = await import('firebase/auth');
      const idToken = await getAuth().currentUser.getIdToken();

      const fnUrl = 'https://us-central1-meetingtraining.cloudfunctions.net/generateMeetingPDF';

      const response = await fetch(fnUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken, meetingId, reportType }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.error || 'PDF generation failed');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = (s) => (s || '').replace(/[^a-z0-9]/gi, '-').replace(/-+/g, '-').toLowerCase();
      const rawDate = meeting.meetingDate;
      const d = rawDate?.toDate ? rawDate.toDate() : rawDate ? new Date(rawDate) : new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const dateCode = `${pad(d.getDate())}${pad(d.getMonth() + 1)}${String(d.getFullYear()).slice(-2)}`;
      const reportLabel = reportType === 'quick' ? 'quick-report' : 'meeting-report';
      a.download = `${slug(meeting.name)}-${dateCode}-${slug(meeting.teamName)}-${reportLabel}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setPdfError(err.message);
      console.error('Download PDF error:', err);
    } finally {
      setGenerating(false);
    }
  };

  const entries = useMemo(() => meeting?.entries || [], [meeting]);
  const reportData = useMemo(
    () => buildReportData(entries, meeting?.inventoryScores || {}, meeting?.weightings || DEFAULT_WEIGHTINGS),
    [entries, meeting?.inventoryScores, meeting?.weightings]
  );
  const awards = reportData.awards;

  if (loading) return <PageLoading text="Loading report..." />;
  if (!meeting) return <p className={styles.loadingText}>Meeting not found.</p>;

  function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }

  // Build tabs based on role
  const tabs = [
    { id: 'compelling_content', label: '💡 Compelling Content', visible: true },
    { id: 'healthy_behaviors', label: '💪 Healthy Behaviors', visible: true },
    { id: 'right_genius', label: '✨ Right Chemistry', visible: true },
    { id: 'simple_process', label: '📋 Simple Process', visible: true },
    { id: 'awards', label: '📝 Additional Notes', visible: true },
    { id: 'quotes', label: '💬 Quotes', visible: isTeamLeaderOrAbove },
  ];

  return (
    <div className="fade-up">
      <div className={styles.navRow}>
        <Link to={isConsultant ? `/meetings/${meeting.teamId}` : '/team-meetings'} className={`link ${styles.backLink}`}>
          ← Back to meetings
        </Link>
        {isConsultant && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/meetings/${meetingId}/record`)}
            >
              ✏️ Edit Meeting
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', borderRadius: 6, overflow: 'visible' }}>
                  <button
                    onClick={() => handleDownloadPDF('v2')}
                    disabled={generating}
                    style={{
                      padding: '11px 14px',
                      background: generating ? '#9ca3af' : '#1e2e45',
                      color: 'white',
                      border: `1.5px solid ${generating ? '#9ca3af' : '#1e2e45'}`,
                      borderRadius: '6px 0 0 6px',
                      fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 13,
                      cursor: generating ? 'not-allowed' : 'pointer',
                      letterSpacing: '0.3px', whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    {generating ? <><span className="spinner" /> Generating…</> : '↓ Download PDF'}
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); if (!generating) setPdfDropdownOpen(o => !o); }}
                    disabled={generating}
                    style={{
                      padding: '11px 10px',
                      background: generating ? '#9ca3af' : '#2a3d5b',
                      color: 'white',
                      border: `1.5px solid ${generating ? '#9ca3af' : '#2a3d5b'}`,
                      borderLeft: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '0 6px 6px 0',
                      cursor: generating ? 'not-allowed' : 'pointer', fontSize: 10,
                    }}
                  >
                    ▾
                  </button>
                </div>
                {pdfDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', right: 0,
                    background: 'white', border: '1px solid var(--border)',
                    borderRadius: 8, zIndex: 200, minWidth: 210,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)', overflow: 'hidden',
                  }}>
                    {[
                      { type: 'v2', label: 'Full Report', sub: 'Landscape · 1280×720' },
                      { type: 'quick', label: 'Quick Report', sub: 'Portrait · 430×932' },
                    ].map(({ type, label, sub }) => (
                      <button
                        key={type}
                        onClick={() => handleDownloadPDF(type)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '10px 14px', border: 'none', background: 'transparent',
                          cursor: 'pointer', borderBottom: '1px solid var(--border)',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-soft)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>{label}</div>
                        <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 1 }}>{sub}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {pdfError && (
                <div style={{ fontSize: 11, color: 'var(--danger)' }}>{pdfError}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Meeting header */}
      <div className="report-header">
        <div className="report-header-meta">
          Meeting Report · {meeting.teamName} · {role ? `(${role})` : ''}
        </div>
        <h2>{meeting.name}</h2>
        <div className="report-header-sub">
          {formatDate(meeting.meetingDate)} · Observed by {meeting.consultantName} · {entries.length} entries
        </div>
        <div className="grid-3">
          <div>
            <div className="report-stat-label">Duration</div>
            <div className="report-stat-value">{formatDuration(getMeetingDurationMs(meeting))}</div>
          </div>
          <div>
            <div className="report-stat-label">Attendees</div>
            {isConsultant ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                {(meeting.attendees || []).map(a => (
                  <span
                    key={a.id || a.name}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '3px 9px', background: 'var(--bg-soft)', border: '1px solid var(--border)', borderRadius: 12, fontSize: '0.78rem', color: 'var(--ink)' }}
                  >
                    {a.displayName || a.name}
                    <button
                      onClick={() => removeAttendee(a)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-muted)', fontSize: '0.8rem', padding: '0 0 0 2px', lineHeight: 1 }}
                      title={`Remove ${a.displayName || a.name}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
                {(meeting.attendees || []).length === 0 && (
                  <span style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', fontStyle: 'italic' }}>No attendees</span>
                )}
              </div>
            ) : (
              <div className="report-stat-value">{meeting.attendees?.length || 0}</div>
            )}
          </div>
          <div>
            <div className="report-stat-label">Participants</div>
            <div className="report-stat-value">{reportData.participation.uniqueSpeakers}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="report-tabs">
        {tabs.filter(t => t.visible).map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`report-tab${activeTab === t.id ? ' active' : ''}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="card">
        {activeTab === 'simple_process' && (
          <>
            <div className="card-title">Simple Process</div>
            <div className="card-subtitle">How well did the meeting follow a clear structure?</div>
            {role === 'team_member' && <SimpleProcessTeamMember meeting={meeting} />}
            {isTeamLeaderOrAbove && <SimpleProcessTeamLeader meeting={meeting} processCodes={reportData.processCodes} />}
            {isConsultant && !isTeamLeaderOrAbove && <SimpleProcessConsultant meeting={meeting} processCodes={reportData.processCodes} />}
            <RolesDisplay categoryKey="simpleProcess" meeting={meeting} />
          </>
        )}

        {activeTab === 'compelling_content' && (
          <>
            <div className="card-title">Compelling Content</div>
            <div className="card-subtitle">Topics discussed, decisions made, and debates had</div>
            {role === 'team_member' && <CompellingContentTeamLeader reportData={reportData} meeting={meeting} entries={entries} showBarChart={false} />}
            {isTeamLeaderOrAbove && <CompellingContentTeamLeader reportData={reportData} meeting={meeting} entries={entries} />}
            {isConsultant && !isTeamLeaderOrAbove && <CompellingContentConsultant reportData={reportData} meeting={meeting} entries={entries} />}
            <RolesDisplay categoryKey="compellingContent" meeting={meeting} />
          </>
        )}

        {activeTab === 'healthy_behaviors' && (
          <>
            <div className="card-title">Healthy Behaviors</div>
            <div className="card-subtitle">Participation dynamics, inquiry vs advocacy, and engagement</div>
            {role === 'team_member' && <HealthyBehaviorsTeamMember reportData={reportData} entries={entries} showFrequency={true} showStatsTable={false} />}
            {isTeamLeaderOrAbove && <HealthyBehaviorsTeamLeader reportData={reportData} entries={entries} />}
            {isConsultant && !isTeamLeaderOrAbove && <HealthyBehaviorsConsultant reportData={reportData} entries={entries} />}
            <RolesDisplay categoryKey="healthyBehaviors" meeting={meeting} />
          </>
        )}

        {activeTab === 'right_genius' && (
          <>
            <div className="card-title">Right Chemistry</div>
            <div className="card-subtitle">Right Chemistry distribution per person, topic, and overall</div>
            {role === 'team_member' && <TheRightGeniusConsultant reportData={reportData} topics={reportData.topics} />}
            {isTeamLeaderOrAbove && <TheRightGeniusConsultant reportData={reportData} topics={reportData.topics} />}
            {isConsultant && !isTeamLeaderOrAbove && <TheRightGeniusConsultant reportData={reportData} topics={reportData.topics} />}
            <RolesDisplay categoryKey="rightGenius" meeting={meeting} />
          </>
        )}

        {activeTab === 'awards' && (
          <>
            <div className="card-title">Additional Notes</div>
            <div className="card-subtitle">Meeting highlights, feedback, and key moments</div>
            <AdditionalNotesSection awards={awards} entries={entries} meeting={meeting} />
          </>
        )}

        {activeTab === 'quotes' && (isTeamLeaderOrAbove || isConsultant || role === 'team_member') && (
          <>
            <div className="card-title">Quotes</div>
            <div className="card-subtitle">
              {isConsultant ? 'Select quotes to include in shareable reports' : 'Consultant-selected quotes'}
            </div>
            {isConsultant && <QuotesConsultant reportData={reportData} entries={entries} meeting={meeting} />}
            {!isConsultant && <QuotesTeamLeader reportData={reportData} meeting={meeting} />}
          </>
        )}
      </div>
    </div>
  );
}
